import React, { useEffect, useState } from 'react';
import { Button, Input, Space, Table } from 'antd';
import Installer from '../../../classes/installer';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';
import { useStore } from 'store';
import SicuriProtettiServices from 'js/data/sicuri-protetti-services';

interface IProps {
    data: Installer;
    installerSel: (installer: Installer) => void;
}

const InstallerSearchForm = (props: IProps) => {
    const { t } = useTranslation();
    const setLoading = useStore((state) => state.setLoading);
    let searchInput: any;

    const [installers, setInstallers] = useState<simpleStruct[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [searchedColumn, setSearchedColumn] = useState<any>('');

    const getColumnSearchProps = (dataIndex: any) => ({
        filterDropdown: (x: any) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={(node) => {
                        searchInput = node;
                    }}
                    placeholder={`Search ${dataIndex}`}
                    value={x.selectedKeys[0]}
                    onChange={(e) => x.setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(x.selectedKeys, x.confirm, dataIndex)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(x.selectedKeys, x.confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => handleReset(x.clearFilters)} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            x.confirm({ closeDropdown: false });
                            setSearchText(x.selectedKeys[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    ></Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: any) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value: any, record: any) =>
            record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
        onFilterDropdownVisibleChange: (visible: any) => {
            if (visible) {
                setTimeout(() => searchInput.select(), 100);
            }
        },
        render: (text: any) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns = [
        {
            title: t('commonClass.province'),
            dataIndex: 'province',
            sorter: (a: any, b: any) => a.province.localeCompare(b.province),
            ...getColumnSearchProps('province'),
        },
        {
            title: t('customerClass.businessName'),
            dataIndex: 'businessName',
            sorter: (a: any, b: any) => a.businessName.localeCompare(b.businessName),
            ...getColumnSearchProps('businessName'),
        },
        {
            title: t('customerClass.address'),
            dataIndex: 'address',
            sorter: (a: any, b: any) => a.address.localeCompare(b.address),
            ...getColumnSearchProps('address'),
        },
        {
            title: t('customerClass.landlinePhone'),
            dataIndex: 'landlinePhone',
            sorter: (a: any, b: any) => a.landlinePhone.localeCompare(b.landlinePhone),
            ...getColumnSearchProps('landlinePhone'),
        },
        {
            title: t('customerClass.cellular'),
            dataIndex: 'mobilePhone',
            sorter: (a: any, b: any) => a.mobilePhone.localeCompare(b.mobilePhone),
            ...getColumnSearchProps('mobilePhone'),
        },
        {
            title: t('customerClass.emailAddress'),
            dataIndex: 'email',
            sorter: (a: any, b: any) => a.email.localeCompare(b.email),
            ...getColumnSearchProps('email'),
        },
    ];

    const handleSearch = (selectedKeys: any, confirm: any, dataIndex: any) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: any) => {
        clearFilters();
        setSearchText('');
    };

    const onInstallerSelected = async (selectedRowKeys: React.Key[], installer: any) => {
        await props.installerSel(
            new Installer(installer[0].key, installer[0].businessName, installer[0].address, installer[0].contacts, installer[0].geoCoord)
        );
    };

    useEffect(() => {
        const getInstallers = async () => {
            setLoading(true);
            const installers = await SicuriProtettiServices.getInstallers();
            let installersList: any[] = installers;
            if (props.data.installerId !== undefined) {
                installersList = installers.filter((installer) => {
                    return props.data.installerId === installer.value.toString();
                });
                installersList = installersList.concat(
                    installers.filter((installer) => {
                        return props.data.installerId !== installer.value.toString();
                    })
                );
            }
            setInstallers(installersList);
            setLoading(false);
        };

        getInstallers();
        // eslint-disable-next-line
    }, []);

    let installerId;
    if (props.data.installerId !== undefined) installerId = props.data.installerId;
    const installersList: any[] = installers;
    return (
        <Table
            rowSelection={{
                type: 'radio',
                defaultSelectedRowKeys: [installerId],
                onChange: onInstallerSelected,
            }}
            columns={columns}
            dataSource={installersList.map((installer) => {
                return {
                    key: installer.value,
                    province: installer.additionalData[4],
                    businessName: installer.label,
                    address:
                        installer.additionalData[1] +
                        ', ' +
                        installer.additionalData[2] +
                        ' - ' +
                        installer.additionalData[0] +
                        ' (' +
                        installer.additionalData[4] +
                        ') - ' +
                        installer.additionalData[3],
                    landlinePhone: installer.additionalData[8],
                    mobilePhone: installer.additionalData[7],
                    email: installer.additionalData[10],
                    geoCoord: [installer.additionalData[5], installer.additionalData[6]],
                    contacts: (
                        <>
                            <strong>{t('customerClass.cellular')} : </strong>
                            {installer.additionalData[7] === '' ? <i>({t('commonClass.notAvailable')})</i> : installer.additionalData[7]}
                            <br />
                            <strong> {t('customerClass.landlinePhone')} : </strong>
                            {installer.additionalData[8] === '' ? <i>({t('commonClass.notAvailable')})</i> : installer.additionalData[8]}
                            <br />
                            <strong>{t('customerClass.emailAddress')} : </strong>
                            {installer.additionalData[10] === '' ? <i>({t('commonClass.notAvailable')})</i> : installer.additionalData[10]}
                            <br />
                            <strong>{t('customerClass.fax')} : </strong>
                            {installer.additionalData[9] === '' ? <i>({t('commonClass.notAvailable')})</i> : installer.additionalData[9]}
                        </>
                    ),
                };
            })}
        ></Table>
    );
};

export default InstallerSearchForm;
